import { NetworkStatus, useQuery } from "@apollo/client";
import RefreshIcon from "@mui/icons-material/Refresh";
import {
  Box,
  CircularProgress,
  DialogContent,
  DialogTitle,
  IconButton,
  Typography,
} from "@mui/material";
import _ from "lodash";
import { ReactElement, useMemo, useState } from "react";

import {
  GetCampaignPreviewMessagesQuery,
  GetCampaignPreviewMessagesQueryVariables,
  PreviewMessageStatus,
} from "~frontend/__generated__/graphql";
import { AsyncWrapper } from "~frontend/components/AsyncWrapper";
import { FlexBox } from "~frontend/components/FlexBox";
import { ExternalLink } from "~frontend/components/Link";
import Table from "~frontend/components/Table";
import { GET_CAMPAIGN_PREVIEW_MESSAGES } from "~frontend/graphql/campaigns";
import { palette } from "~frontend/theme";
import { prettyPhoneNumber } from "~frontend/util/phoneNumber";

type TableRow = {
  id: string;
  contactPhone: string;
  contactName: string | null;
  contactOrder: number;
  variants: {
    id: string;
    title: string;
    status: PreviewMessageStatus;
    errorCode: string | null;
    sentAt: Date;
    deliveredAt: Date | null;
    provider: string;
  }[];
};

type PreviewMessage =
  GetCampaignPreviewMessagesQuery["getCampaignByShortId"]["previewMessages"][0];

function formatTableRows(data: PreviewMessage[]): TableRow[] {
  // batchId -> batchContactOrder -> messages
  const messagesByBatchAndContact: Record<
    string,
    Record<number, PreviewMessage[]>
  > = {};

  // group by batchId and contactPhone
  data.forEach(message => {
    if (!messagesByBatchAndContact[message.batchId]) {
      messagesByBatchAndContact[message.batchId] = {};
    }

    if (
      !messagesByBatchAndContact[message.batchId][message.batchContactOrder]
    ) {
      messagesByBatchAndContact[message.batchId][message.batchContactOrder] =
        [];
    }

    messagesByBatchAndContact[message.batchId][message.batchContactOrder].push(
      message
    );
  });

  // batchId -> TableRow[]
  const rowsByBatch: Record<string, TableRow[]> = _.mapValues(
    messagesByBatchAndContact,
    messagesByContact =>
      Object.values(messagesByContact)
        .map(messages => {
          const sortedMessages = _.sortBy(messages, "scriptVariantOrder");
          const {
            contactPhoneNumber,
            previewMessageContactName,
            batchContactOrder,
            batchId,
          } = sortedMessages[0];

          return {
            id: `${batchId}-${contactPhoneNumber}`,
            contactPhone: contactPhoneNumber,
            contactName: previewMessageContactName || null,
            contactOrder: batchContactOrder,
            variants: sortedMessages.map(message => ({
              id: message.id,
              title: `Variant ${message.scriptVariantOrder + 1}`,
              status: message.status,
              errorCode: message.providerErrorCode || null,
              sentAt: message.createdAt,
              deliveredAt: message.deliveryReportCreatedAt || null,
              provider: message.provider,
            })),
          };
        })
        .sort((a, b) => a.contactOrder - b.contactOrder)
  );

  // sort batches by when they were sent
  return _.sortBy(
    Object.values(rowsByBatch),
    rows => -rows[0].variants[0].sentAt
  ).flat();
}

function MessageStatusDisplay({
  provider,
  status,
  errorCode,
}: {
  provider: string;
  status: PreviewMessageStatus;
  errorCode: string | null;
}): ReactElement {
  switch (status) {
    case PreviewMessageStatus.Sent:
      return <>Sent</>;
    case PreviewMessageStatus.Delivered:
      return <>Delivered</>;
    case PreviewMessageStatus.Queued:
      return <>Queued</>;
    case PreviewMessageStatus.Unknown:
      return <>Unknown</>;
    case PreviewMessageStatus.Failed:
      return (
        <ExternalLink href="https://support.scaletowin.com/error-codes-shaft-violations-and-filtering-in-text-campaigns">
          Error Received from {_.capitalize(provider)} ({errorCode})
        </ExternalLink>
      );
  }
}

export function CampaignPreviewMessagesTable({
  campaignShortId,
  alwaysShowVariant = false,
  dialogLayout = false,
}: {
  campaignShortId: number;
  alwaysShowVariant?: boolean;
  dialogLayout?: boolean;
}): ReactElement {
  const { loading, networkStatus, data, error, refetch } = useQuery<
    GetCampaignPreviewMessagesQuery,
    GetCampaignPreviewMessagesQueryVariables
  >(GET_CAMPAIGN_PREVIEW_MESSAGES, {
    variables: {
      input: {
        shortId: campaignShortId,
      },
    },
    notifyOnNetworkStatusChange: true,
  });

  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(10);

  const rows: TableRow[] | undefined = useMemo(
    () =>
      data
        ? formatTableRows(data.getCampaignByShortId.previewMessages)
        : undefined,
    [data]
  );

  const header = (
    <FlexBox flexDirection="row" justifyContent="space-between">
      {dialogLayout ? (
        <Box>Preview Text Send History</Box>
      ) : (
        <Typography variant="body1">Preview Text Status</Typography>
      )}
      <Box sx={dialogLayout ? {} : { mt: "-12px" }}>
        <IconButton
          onClick={() => refetch()}
          disabled={loading || networkStatus === NetworkStatus.refetch}
        >
          {networkStatus === NetworkStatus.refetch ? (
            <CircularProgress size={24} />
          ) : (
            <RefreshIcon />
          )}
        </IconButton>
      </Box>
    </FlexBox>
  );

  const body = (
    <>
      <AsyncWrapper
        loading={networkStatus === NetworkStatus.loading}
        errors={[error]}
        data={rows}
      >
        {rows => (
          <>
            <Table
              rows={rows.slice(page * pageSize, (page + 1) * pageSize)}
              emptyState="No preview messages sent"
              columns={[
                {
                  key: "contactPhone",
                  title: "Number",
                  renderValue: row => (
                    <span>
                      {prettyPhoneNumber(row.contactPhone)}
                      {row.contactName ? ` (${row.contactName})` : ""}
                    </span>
                  ),
                  sx: { verticalAlign: "top" },
                },
                {
                  key: "variants",
                  title: "Status",
                  align: "left",
                  renderValue: row =>
                    row.variants.map(variant => (
                      <Box key={variant.id} sx={{ mb: 1 }}>
                        {(alwaysShowVariant || row.variants.length > 1) && (
                          <Typography
                            variant="label3Caps"
                            sx={{ color: palette.darkGray }}
                          >
                            {variant.title}
                          </Typography>
                        )}
                        <Typography variant="body2">
                          <MessageStatusDisplay
                            provider={variant.provider}
                            status={variant.status}
                            errorCode={variant.errorCode}
                          />
                        </Typography>
                        <Typography variant="body3">
                          Sent: {new Date(variant.sentAt).toLocaleString()}
                          {variant.deliveredAt && (
                            <>
                              <br />
                              Delivered:{" "}
                              {new Date(variant.deliveredAt).toLocaleString()}
                            </>
                          )}
                        </Typography>
                      </Box>
                    )),
                },
              ]}
              totalCount={rows.length}
              rowsPerPage={pageSize}
              onChangeRowsPerPage={newRowsPerPage => {
                setPageSize(newRowsPerPage);
                setPage(0);
              }}
              page={page}
              onChangePage={setPage}
            />
          </>
        )}
      </AsyncWrapper>
    </>
  );

  if (dialogLayout) {
    return (
      <>
        <DialogTitle>{header}</DialogTitle>
        <DialogContent>{body}</DialogContent>
      </>
    );
  } else {
    return (
      <>
        {header}
        {body}
      </>
    );
  }
}
